/**
 * SettlementMapView — SVG renderer for settlement layouts.
 * Renders districts, buildings, roads, walls, water, and entry points.
 */

import { useState, useCallback, useRef, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Info, X } from 'lucide-react';
import {
  rectCorners,
  DISTRICT_COLORS,
  BUILDING_COLORS,
  type Settlement,
  type Building,
  type District,
  type Point,
  type RoadTier,
} from '@omen-quest/settlement-generator';

// ============================================================================
// TYPES
// ============================================================================

interface ViewState {
  panX: number;
  panY: number;
  zoom: number;
}

interface DragState {
  isDragging: boolean;
  startX: number;
  startY: number;
  startPanX: number;
  startPanY: number;
}

interface SettlementMapViewProps {
  settlement: Settlement;
  className?: string;
  onBuildingClick?: (building: Building) => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const MIN_ZOOM = 0.3;
const MAX_ZOOM = 5;
const ZOOM_SENSITIVITY = 0.001;

const ROAD_WIDTHS: Record<RoadTier, number> = {
  main: 3,
  secondary: 2,
  alley: 1,
};

const ROAD_COLORS: Record<RoadTier, string> = {
  main: '#A0896C',
  secondary: '#B8A080',
  alley: '#C8B898',
};

// ============================================================================
// COMPONENT
// ============================================================================

export function SettlementMapView({ settlement, className, onBuildingClick }: SettlementMapViewProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [view, setView] = useState<ViewState>({ panX: 0, panY: 0, zoom: 1 });
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false, startX: 0, startY: 0, startPanX: 0, startPanY: 0,
  });
  const [hoveredBuilding, setHoveredBuilding] = useState<Building | null>(null);
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // Compute viewBox from settlement bounds
  const viewBox = useMemo(() => {
    const { minX, minY, maxX, maxY } = settlement.bounds;
    const pad = 30;
    return `${minX - pad} ${minY - pad} ${maxX - minX + pad * 2} ${maxY - minY + pad * 2}`;
  }, [settlement.bounds]);

  // Pan/zoom handlers
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const delta = -e.deltaY * ZOOM_SENSITIVITY;
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, view.zoom * (1 + delta)));
    const zoomFactor = newZoom / view.zoom;
    setView({
      zoom: newZoom,
      panX: mouseX - (mouseX - view.panX) * zoomFactor,
      panY: mouseY - (mouseY - view.panY) * zoomFactor,
    });
  }, [view]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 1 || e.button === 0) {
      setDragState({
        isDragging: true,
        startX: e.clientX,
        startY: e.clientY,
        startPanX: view.panX,
        startPanY: view.panY,
      });
    }
  }, [view.panX, view.panY]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragState.isDragging) return;
    const dx = e.clientX - dragState.startX;
    const dy = e.clientY - dragState.startY;
    setView(v => ({ ...v, panX: dragState.startPanX + dx, panY: dragState.startPanY + dy }));
  }, [dragState]);

  const handleMouseUp = useCallback(() => {
    setDragState(s => ({ ...s, isDragging: false }));
  }, []);

  // Building click handler
  const handleBuildingClick = useCallback((building: Building, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedBuilding(prev => prev?.id === building.id ? null : building);
    onBuildingClick?.(building);
  }, [onBuildingClick]);

  const handleBuildingHover = useCallback((building: Building | null, e?: React.MouseEvent) => {
    setHoveredBuilding(building);
    if (e) {
      setTooltipPos({ x: e.clientX, y: e.clientY });
    }
  }, []);

  return (
    <div className={cn('relative overflow-hidden bg-[#1a1a1a] rounded-lg', className)}>
      <svg
        ref={svgRef}
        viewBox={viewBox}
        className="w-full h-full cursor-grab active:cursor-grabbing"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ touchAction: 'none' }}
      >
        <g transform={`translate(${view.panX}, ${view.panY}) scale(${view.zoom})`}>
          {/* Layer 1: Water features (bottom) */}
          {settlement.waterFeatures.map(water => (
            <polyline
              key={water.id}
              points={pointsToSvg(water.points)}
              fill="none"
              stroke={water.type === 'coast' ? '#2A6496' : '#4A90B8'}
              strokeWidth={water.width}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={water.type === 'coast' ? 0.4 : 0.6}
            />
          ))}

          {/* Layer 2: District boundaries */}
          {settlement.districts.map(district => (
            <polygon
              key={district.id}
              points={pointsToSvg(district.boundary)}
              fill={DISTRICT_COLORS[district.type]}
              fillOpacity={0.25}
              stroke={DISTRICT_COLORS[district.type]}
              strokeWidth={0.5}
              strokeOpacity={0.6}
            />
          ))}

          {/* Layer 3: Roads */}
          {settlement.roads.map(road => (
            <polyline
              key={road.id}
              points={pointsToSvg(road.points)}
              fill="none"
              stroke={ROAD_COLORS[road.tier]}
              strokeWidth={ROAD_WIDTHS[road.tier]}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={0.7}
            />
          ))}

          {/* Layer 4: Walls */}
          {settlement.walls.map(wall => (
            <polyline
              key={wall.id}
              points={pointsToSvg(wall.points)}
              fill="none"
              stroke={wall.tier === 'inner' ? '#8B7355' : '#5C4033'}
              strokeWidth={wall.tier === 'inner' ? 2 : 3}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}

          {/* Layer 5: Gates */}
          {settlement.gates.map(gate => (
            <g key={gate.id}>
              <circle
                cx={gate.position.x}
                cy={gate.position.y}
                r={3}
                fill="#D4A574"
                stroke="#5C4033"
                strokeWidth={1}
              />
              <rect
                x={gate.position.x - 4}
                y={gate.position.y - 1.5}
                width={8}
                height={3}
                fill="#D4A574"
                stroke="#5C4033"
                strokeWidth={0.5}
                transform={`rotate(${(gate.rotation * 180) / Math.PI}, ${gate.position.x}, ${gate.position.y})`}
              />
            </g>
          ))}

          {/* Layer 6: Buildings */}
          {settlement.buildings.map(building => {
            const corners = rectCorners(building.footprint);
            const isHovered = hoveredBuilding?.id === building.id;
            const isSelected = selectedBuilding?.id === building.id;
            return (
              <g
                key={building.id}
                className="cursor-pointer"
                onClick={(e) => handleBuildingClick(building, e)}
                onMouseEnter={(e) => handleBuildingHover(building, e)}
                onMouseLeave={() => handleBuildingHover(null)}
              >
                <polygon
                  points={pointsToSvg(corners)}
                  fill={BUILDING_COLORS[building.category]}
                  stroke={isSelected ? '#FFD700' : isHovered ? '#FFFFFF' : '#00000044'}
                  strokeWidth={isSelected ? 1 : isHovered ? 0.8 : 0.3}
                  fillOpacity={0.85}
                />
                {/* Building label at higher zoom */}
                <text
                  x={building.footprint.center.x}
                  y={building.footprint.center.y + 0.5}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="#000000"
                  fillOpacity={0.7}
                  fontSize={2}
                  fontFamily="monospace"
                  pointerEvents="none"
                >
                  {building.category.charAt(0).toUpperCase()}
                </text>
              </g>
            );
          })}

          {/* Layer 7: District labels */}
          {settlement.districts.map(district => (
            <text
              key={`label_${district.id}`}
              x={district.center.x}
              y={district.center.y}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#FFFFFF"
              fillOpacity={0.6}
              fontSize={4}
              fontFamily="sans-serif"
              fontWeight="bold"
              pointerEvents="none"
            >
              {district.flavoredName || district.placeholderName}
            </text>
          ))}

          {/* Layer 8: Entry point indicators */}
          {settlement.entryPoints.map(entry => {
            const angle = entry.direction + Math.PI; // point inward
            const tipX = entry.position.x + Math.cos(angle) * 8;
            const tipY = entry.position.y + Math.sin(angle) * 8;
            return (
              <g key={entry.id}>
                <line
                  x1={entry.position.x}
                  y1={entry.position.y}
                  x2={tipX}
                  y2={tipY}
                  stroke="#CCAA88"
                  strokeWidth={2}
                  markerEnd="url(#arrowhead)"
                />
                <text
                  x={entry.position.x}
                  y={entry.position.y - 5}
                  textAnchor="middle"
                  fill="#CCAA88"
                  fontSize={3}
                  fontFamily="sans-serif"
                  pointerEvents="none"
                >
                  {entry.label}
                </text>
              </g>
            );
          })}

          {/* SVG defs for markers */}
          <defs>
            <marker id="arrowhead" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
              <polygon points="0 0, 6 2, 0 4" fill="#CCAA88" />
            </marker>
          </defs>
        </g>
      </svg>

      {/* Hover tooltip */}
      {hoveredBuilding && !selectedBuilding && (
        <div
          className="absolute pointer-events-none bg-black/90 border border-white/20 rounded px-2 py-1 text-xs text-white z-10"
          style={{ left: tooltipPos.x + 12, top: tooltipPos.y + 12, position: 'fixed' }}
        >
          <div className="font-bold">
            {hoveredBuilding.flavoredName || hoveredBuilding.placeholderName}
          </div>
          <div className="text-neutral-400">{hoveredBuilding.category.replace(/_/g, ' ')}</div>
        </div>
      )}

      {/* Selected building panel */}
      {selectedBuilding && (
        <BuildingPanel
          building={selectedBuilding}
          district={settlement.districts.find(d => d.id === selectedBuilding.districtId)}
          onClose={() => setSelectedBuilding(null)}
        />
      )}
    </div>
  );
}

// ============================================================================
// BUILDING INFO PANEL
// ============================================================================

function BuildingPanel({
  building,
  district,
  onClose,
}: {
  building: Building;
  district?: District;
  onClose: () => void;
}) {
  return (
    <div className="absolute top-2 right-2 w-64 bg-black/95 border border-white/20 rounded-lg p-3 text-sm z-20">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h3 className="font-bold text-white">
            {building.flavoredName || building.placeholderName}
          </h3>
          <div className="text-neutral-400 text-xs">{building.category.replace(/_/g, ' ')}</div>
        </div>
        <button onClick={onClose} className="text-neutral-400 hover:text-white p-0.5">
          <X size={14} />
        </button>
      </div>

      {(building.flavoredDescription || building.placeholderDescription) && (
        <p className="text-neutral-300 text-xs mb-2">
          {building.flavoredDescription || building.placeholderDescription}
        </p>
      )}

      {district && (
        <div className="text-xs text-neutral-500 mb-2">
          <Info size={10} className="inline mr-1" />
          {district.flavoredName || district.placeholderName}
        </div>
      )}

      {building.services.length > 0 && (
        <div className="mb-2">
          <div className="text-neutral-500 text-xs font-bold mb-1">Services</div>
          {building.services.map((svc, i) => (
            <div key={i} className="text-xs text-neutral-300 flex items-center gap-1">
              <span className="text-neutral-500">{svc.type}</span>
              <span>— {svc.description}</span>
            </div>
          ))}
        </div>
      )}

      {building.npcSlots.length > 0 && (
        <div>
          <div className="text-neutral-500 text-xs font-bold mb-1">NPCs</div>
          {building.npcSlots.map((npc, i) => (
            <div key={i} className="text-xs text-neutral-300">
              <span className="text-neutral-400">{npc.role}:</span>{' '}
              {npc.flavoredName || npc.placeholderName}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function pointsToSvg(points: Point[]): string {
  return points.map(p => `${p.x},${p.y}`).join(' ');
}
